import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col } from 'reactstrap';

const ThrottledState = ({ retryAfter, onRetry, onBack, translate }) => {
  const [timeLeft, setTimeLeft] = useState(retryAfter);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    setTimeLeft(retryAfter);
  }, [retryAfter]);

  const baseline = 60;
  const progress = Math.min(100, (timeLeft / baseline) * 100);

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: '60vh' }}
    >
      <Row className="justify-content-center text-center">
        <Col md="8" lg="6">
          <div className="mb-5">
            <svg
              width="240"
              height="180"
              viewBox="0 0 240 180"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="img-fluid"
            >
              {/* Abstract representation of "Slow Down / Calm State" */}
              <circle
                cx="120"
                cy="90"
                r="80"
                fill="url(#paint0_radial)"
                fillOpacity="0.05"
              />
              <circle
                cx="120"
                cy="90"
                r="50"
                stroke="currentColor"
                strokeWidth="2"
                strokeOpacity="0.1"
                fill="none"
              />
              <path
                d="M120 40C92.3858 40 70 62.3858 70 90C70 117.614 92.3858 140 120 140C147.614 140 170 117.614 170 90C170 62.3858 147.614 40 120 40Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="text-muted"
                style={{
                  strokeDasharray: 314.15,
                  strokeDashoffset: 314.15 - (progress / 100) * 314.15,
                  transition: 'stroke-dashoffset 1s linear',
                  strokeOpacity: 0.3
                }}
              />
              {/* Speedometer needle representing 60s total */}
              <line
                x1="120"
                y1="90"
                x2="120"
                y2="50"
                stroke="var(--orange)"
                strokeWidth="4"
                strokeLinecap="round"
                style={{
                  transform: `rotate(${
                    (360 * (baseline - timeLeft)) / baseline
                  }deg)`,
                  transformOrigin: '120px 90px',
                  transition: 'transform 1s linear'
                }}
              />
              <circle cx="120" cy="90" r="6" fill="var(--orange)" />

              {/* Calm ripples */}
              <circle
                cx="120"
                cy="90"
                r="20"
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity="0.2"
                className="text-primary"
              >
                <animate
                  attributeName="r"
                  from="20"
                  to="50"
                  dur="3s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  from="0.2"
                  to="0"
                  dur="3s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle
                cx="120"
                cy="90"
                r="20"
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity="0.2"
                className="text-primary"
              >
                <animate
                  attributeName="r"
                  from="20"
                  to="50"
                  dur="3s"
                  begin="1.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="stroke-opacity"
                  from="0.2"
                  to="0"
                  dur="3s"
                  begin="1.5s"
                  repeatCount="indefinite"
                />
              </circle>

              <defs>
                <radialGradient
                  id="paint0_radial"
                  cx="0"
                  cy="0"
                  r="1"
                  gradientUnits="userSpaceOnUse"
                  gradientTransform="translate(120 90) rotate(90) scale(80)"
                >
                  <stop stopColor="currentColor" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          <h2 className="mb-3 font-weight-bold">
            {translate('throttled_heading')}
          </h2>
          <p
            className="text-muted mb-4 mx-auto"
            style={{ maxWidth: '100%', textAlign: 'left' }}
          >
            {translate('throttled_text')}
          </p>

          <div className="d-flex justify-content-center gap-3">
            <Button
              color="secondary"
              className="px-5 mr-3"
              style={{ minWidth: '180px' }}
              onClick={onBack}
            >
              {translate('throttled_button_back')}
            </Button>
            <Button
              color="orange"
              className="px-3 py-2"
              style={{ minWidth: '180px' }}
              onClick={onRetry}
              disabled={timeLeft > 0}
            >
              {timeLeft > 0
                ? translate('throttled_retry_after').replace('%s', timeLeft)
                : translate('throttled_button_retry')}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ThrottledState;
